import type { PlacementComponent } from '@post-office/placement-contexts/create-placement-component/types';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';

import { useInitialData } from '@post-office/placement-contexts/hooks';

import React, { useEffect } from 'react';

import { MessageRenderer } from '../__generated__/client/render';

import { PLACEMENT_ID } from '../__generated__/common';

import { withJiraIssueviewDescriptionIntlProvider } from '../__generated__/intl-provider';

import {
	type JiraIssueviewDescriptionInitialData,
	type JiraIssueviewDescriptionMessage,
} from '../api/types';

import { type Placement as JiraIssueviewDescriptionPlacement } from '../contracts/Placement';

export const JiraIssueviewDescriptionPlacementComponent: PlacementComponent<
	Record<string, unknown>
> = withJiraIssueviewDescriptionIntlProvider(
	createPlacementComponent<JiraIssueviewDescriptionPlacement>(
		PLACEMENT_ID,
		({ initialData, onClick, onDismissed, onAdsShown }) => {
			const { isLoading, data } = useInitialData<JiraIssueviewDescriptionInitialData>(initialData);
			const [messages, setMessages] = React.useState<JiraIssueviewDescriptionMessage[]>([]);
			const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

			const handleAdDismissed = React.useCallback(
				(messageTemplateId: string) => {
					setMessages((prevMessages) =>
						prevMessages.filter((message) => message.messageTemplateId !== messageTemplateId),
					);
					onDismissed(messageTemplateId);
					fireAnalyticsEvent({
						eventType: 'ui',
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'description-xflow-dismiss',
						attributes: {
							messageTemplateId,
						},
					});
				},
				[onDismissed, fireAnalyticsEvent],
			);

			const handleAdClick = React.useCallback(
				(messageTemplateId: string) => {
					onClick(messageTemplateId);
					fireAnalyticsEvent({
						eventType: 'ui',
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'description-xflow-click',
						attributes: {
							messageTemplateId,
						},
					});
				},
				[onClick, fireAnalyticsEvent],
			);

			useEffect(() => {
				if (data?.messages.length) {
					const visibleAds = data.messages.map((message) => message.messageTemplateId);
					onAdsShown(visibleAds);
					setMessages(data.messages);
					const attributes = data.messages.reduce<Record<string, boolean>>((acc, val) => {
						acc[val.messageTemplateId] = true;
						return acc;
					}, {});
					fireAnalyticsEvent({
						eventType: 'screen',
						name: 'description-xflow',
						attributes,
					});
				} else {
					setMessages([]);
				}
			}, [data?.messages, onAdsShown, fireAnalyticsEvent]);

			if (isLoading || !data?.messages.length) {
				return null;
			}

			return (
				<div data-testid="jira-issueview-description-placement">
					{messages.map((message) => (
						<MessageRenderer
							{...message}
							id={message.messageInstanceId}
							onDismissed={handleAdDismissed}
							onClick={handleAdClick}
						/>
					))}
				</div>
			);
		},
	),
);

export default JiraIssueviewDescriptionPlacementComponent;
